// src/modules/app-views/invoices/pages/Document/tabs/PreviewTab/FiscalInfoSection.jsx
// *******************************************************************************
// FISCAL INFO SECTION - DANFE NFC-e
// *******************************************************************************
// Displays fiscal information: document number, series, issue date,
// access key, QR Code, authorization protocol.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Typography, QRCode, Button, message } from 'antd';
import { CopyOutlined, LinkOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import translateX from 'utils/translateX';

const { Text, Paragraph } = Typography;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const FiscalInfoSection = ({ data }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const documentNumber = data?.documentNumber;
  const documentSerie = data?.documentSerie || 1;
  const issueDate = data?.issueDate;
  const accessKey = data?.accessKey;
  const authorizationProtocol = data?.authorizationProtocol || data?.protocol;
  const authorizationDate = data?.authorizationDate || data?.protocolDate;
  const qrCodeUrl = data?.qrCodeUrl || data?.qrCode;
  const consultUrl = data?.consultUrl;

  // Get QR Code image from avatar (real QR Code from fiscal provider)
  const qrCodeImage = data?.avatar?.storageUrl || data?.avatar?.thumbUrl || data?.avatar?.base64Thumb;

  const isNfe = data?.documentType === 'nfe';

  // Build QR Code value - use qrCodeUrl if available, otherwise build from accessKey
  // Only needed when there's no QR Code image from avatar
  const qrCodeValue = !qrCodeImage ? buildQrCodeValue({ qrCodeUrl, accessKey, data, isNfe }) : null;

  // Format dates
  const formattedIssueDate = issueDate ? dayjs(issueDate).format('DD/MM/YYYY HH:mm:ss') : '-';
  const formattedAuthDate = authorizationDate
    ? dayjs(authorizationDate).format('DD/MM/YYYY HH:mm:ss')
    : '-';

  // Format document number
  const formattedNumber = documentNumber ? String(documentNumber).padStart(9, '0') : '-';
  const formattedSerie = String(documentSerie).padStart(3, '0');

  // Format access key in groups of 4 digits
  const formattedAccessKey = accessKey ? formatAccessKey(accessKey) : null;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleCopyAccessKey = () => {
    if (accessKey) {
      navigator.clipboard.writeText(accessKey);
      message.success(translateX('access_key_copied'));
    }
  };

  const handleOpenConsult = () => {
    if (consultUrl) {
      window.open(consultUrl, '_blank');
    } else if (accessKey) {
      const url = isNfe
        ? `https://www.nfe.fazenda.gov.br/portal/consulta.aspx?tipoConsulta=completa&nfe=${accessKey}`
        : `https://www.nfce.fazenda.sp.gov.br/consulta`;
      window.open(url, '_blank');
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="danfe-section danfe-fiscal-info">
      {/* Document Info */}
      <div className="danfe-doc-info">
        <div className="danfe-doc-info-row">
          <Text style={{ fontSize: 11 }}>
            <strong>Nº:</strong> {formattedNumber}
          </Text>
          <Text style={{ fontSize: 11 }}>
            <strong>Série:</strong> {formattedSerie}
          </Text>
        </div>
        <Text style={{ fontSize: 11, display: 'block' }}>
          <strong>Emissão:</strong> {formattedIssueDate}
        </Text>
      </div>

      {/* Access Key */}
      {accessKey && (
        <div className="danfe-access-key">
          <Text strong style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
            CHAVE DE ACESSO
          </Text>
          <Paragraph
            style={{ fontSize: 10, fontFamily: 'monospace', margin: '4px 0', wordBreak: 'break-all' }}
            copyable={{
              text: accessKey,
              tooltips: [translateX('copy'), translateX('copied')],
            }}
          >
            {formattedAccessKey}
          </Paragraph>
        </div>
      )}

      {/* QR Code - Use real image from avatar if available, otherwise generate with antd */}
      {(qrCodeImage || qrCodeValue) && (
        <div className="danfe-qrcode">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginTop: 12 }}>
            {/* Real QR Code image from fiscal provider (avatar) */}
            {qrCodeImage ? (
              <img
                src={qrCodeImage}
                alt="QR Code NFC-e"
                style={{
                  width: 100,
                  height: 100,
                  objectFit: 'contain',
                }}
              />
            ) : (
              /* Fallback: Generate QR Code with antd */
              <QRCode
                value={qrCodeValue}
                size={100}
                bordered={false}
                errorLevel="M"
                type="svg"
              />
            )}
            <div style={{ textAlign: 'left' }}>
              {/* Interactive link - hidden on print */}
              <div className="no-print">
                <Text style={{ fontSize: 10, display: 'block' }}>
                  Consulte pela chave de acesso em:
                </Text>
                <Button
                  type="link"
                  size="small"
                  icon={<LinkOutlined />}
                  onClick={handleOpenConsult}
                  style={{ fontSize: 10, padding: 0, height: 'auto' }}
                >
                  {isNfe ? 'Portal da NF-e' : 'Portal da NFC-e'}
                </Button>
              </div>
              {/* Static text for print */}
              <div className="print-only">
                <Text style={{ fontSize: 10, display: 'block' }}>
                  Consulte pela chave de acesso em:
                </Text>
                <Text style={{ fontSize: 9, display: 'block', marginTop: 2 }}>
                  www.nfce.fazenda.sp.gov.br
                </Text>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* No QR Code - Show copy button */}
      {!qrCodeImage && !qrCodeValue && accessKey && (
        <div className="danfe-no-qrcode" style={{ textAlign: 'center', marginTop: 8 }}>
          <Button
            type="link"
            size="small"
            icon={<CopyOutlined />}
            onClick={handleCopyAccessKey}
            className="no-print"
          >
            {translateX('copy_access_key')}
          </Button>
        </div>
      )}

      {/* Authorization Protocol */}
      {authorizationProtocol && (
        <div className="danfe-protocol" style={{ marginTop: 12 }}>
          <Text style={{ fontSize: 10, display: 'block' }}>
            <strong>Protocolo:</strong> {authorizationProtocol}
          </Text>
          <Text style={{ fontSize: 10, display: 'block' }}>
            <strong>Data Autorização:</strong> {formattedAuthDate}
          </Text>
        </div>
      )}

      {/* Environment indicator (if homologation) */}
      {data?.environment === 'homologation' && (
        <div
          className="danfe-homologation"
          style={{
            marginTop: 12,
            padding: '4px 8px',
            background: '#fff7e6',
            border: '1px solid #ffd591',
            borderRadius: 4,
            textAlign: 'center',
          }}
        >
          <Text strong style={{ fontSize: 10, color: '#d46b08' }}>
            EMITIDA EM AMBIENTE DE HOMOLOGAÇÃO - SEM VALOR FISCAL
          </Text>
        </div>
      )}
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default FiscalInfoSection;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Format access key in groups of 4 digits for better readability
 * @param {string} accessKey - 44-digit access key
 * @returns {string} Formatted access key
 */
function formatAccessKey(accessKey) {
  if (!accessKey) return '';

  // Remove any non-numeric characters
  const cleanKey = accessKey.replace(/\D/g, '');

  // Split into groups of 4
  const groups = [];
  for (let i = 0; i < cleanKey.length; i += 4) {
    groups.push(cleanKey.substring(i, i + 4));
  }

  return groups.join(' ');
}

/**
 * Build QR Code value for NFC-e or NF-e
 * Priority: qrCodeUrl > generated URL from accessKey
 * For NF-e: generates URL to national SEFAZ portal (nfe.fazenda.gov.br)
 * For NFC-e: generates URL to state-specific SEFAZ portal
 * @param {Object} params - Parameters
 * @param {string} params.qrCodeUrl - Direct QR Code URL from backend
 * @param {string} params.accessKey - 44-digit access key
 * @param {Object} params.data - Invoice data
 * @param {boolean} params.isNfe - Whether this is a NF-e (model 55)
 * @returns {string|null} QR Code value or null
 */
function buildQrCodeValue({ qrCodeUrl, accessKey, data, isNfe }) {
  // Use qrCodeUrl only when it is a real URL (NFC-e providers return a full URL;
  // NF-e providers may return the raw access key string in the qrCode field)
  if (qrCodeUrl && qrCodeUrl.startsWith('http')) {
    return qrCodeUrl;
  }

  // If no accessKey, cannot generate QR Code
  if (!accessKey) {
    return null;
  }

  // NF-e (model 55): use national SEFAZ portal - works for all states
  if (isNfe) {
    return `https://www.nfe.fazenda.gov.br/portal/consulta.aspx?tipoConsulta=completa&nfe=${accessKey}`;
  }

  // Extract UF code from accessKey (positions 0-1)
  const ufCode = accessKey.substring(0, 2);

  // Map UF codes to SEFAZ NFC-e consultation URLs
  const sefazUrls = {
    // North
    '11': 'http://www.nfce.sefin.ro.gov.br/consultanfce/consulta.jsp', // RO
    '12': 'http://www.sefaznet.ac.gov.br/nfce/consulta', // AC
    '13': 'https://sistemas.sefaz.am.gov.br/nfceweb/formConsulta.do', // AM
    '14': 'https://www.sefaz.rr.gov.br/nfce/servlet/wp_consulta_nfce', // RR
    '15': 'https://www.sefa.pa.gov.br/nfce-homologacao/consulta', // PA
    '16': 'https://www.sefaz.ap.gov.br/nfce/nfcep.php', // AP
    '17': 'http://www.sefaz.to.gov.br/nfce/consulta.jsf', // TO
    // Northeast
    '21': 'http://www.nfce.sefaz.ma.gov.br/portal/consultarNFCe.jsp', // MA
    '22': 'http://www.sefaz.pi.gov.br/nfce/consulta', // PI
    '23': 'http://nfce.sefaz.ce.gov.br/pages/showNFCe.html', // CE
    '24': 'http://www.set.rn.gov.br/nfce/consulta', // RN
    '25': 'https://www.sefaz.pb.gov.br/nfce', // PB
    '26': 'http://nfce.sefaz.pe.gov.br/nfce/consulta', // PE
    '27': 'http://nfce.sefaz.al.gov.br/consultaNFCe.htm', // AL
    '28': 'http://www.nfce.se.gov.br/portal/consultarNFCe.jsp', // SE
    '29': 'http://nfe.sefaz.ba.gov.br/servicos/nfce/default.aspx', // BA
    // Southeast
    '31': 'http://nfce.fazenda.mg.gov.br/portalnfce', // MG
    '32': 'http://app.sefaz.es.gov.br/ConsultaNFCe', // ES
    '33': 'http://www.nfce.fazenda.rj.gov.br/consulta', // RJ
    '35': 'https://www.nfce.fazenda.sp.gov.br/consulta', // SP
    // South
    '41': 'http://www.sped.fazenda.pr.gov.br/nfce/qrcode', // PR
    '42': 'https://sat.sef.sc.gov.br/nfce/consulta', // SC
    '43': 'https://www.sefaz.rs.gov.br/NFCE/NFCE-COM.aspx', // RS
    // Central-West
    '50': 'http://www.dfe.ms.gov.br/nfce', // MS
    '51': 'https://www.sefaz.mt.gov.br/nfce/consultanfce', // MT
    '52': 'http://www.sefaz.go.gov.br/nfce/consulta', // GO
    '53': 'http://dec.fazenda.df.gov.br/ConsultarNFCe.aspx', // DF
  };

  // Get SEFAZ URL for UF or use generic
  const baseUrl = sefazUrls[ufCode] || `https://www.nfce.fazenda.sp.gov.br/consulta`;

  // For simplicity, return the accessKey as QR Code value
  // The actual NFC-e QR Code URL format varies by state and includes hash/signature
  // In production, the qrCodeUrl should come from the SEFAZ response
  return accessKey;
}
